const VERTEX_SHADER = `
attribute vec2 a_position;
varying vec2 _uv;
void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
  _uv = (a_position + 1.0) / 2.0;
}
`

function buildFragmentShader(transitionGlsl: string): string {
  return `
precision mediump float;
varying vec2 _uv;
uniform float progress;
uniform float ratio;
uniform sampler2D from, to;
vec4 getFromColor(vec2 uv) { return texture2D(from, uv); }
vec4 getToColor(vec2 uv) { return texture2D(to, uv); }
${transitionGlsl}
void main() {
  gl_FragColor = transition(_uv);
}
`
}

function compileShader(gl: WebGLRenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type)!
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(shader)
    gl.deleteShader(shader)
    throw new Error(`Shader compile error: ${info}`)
  }
  return shader
}

export interface GLProgram {
  program: WebGLProgram
  locations: {
    position: number
    progress: WebGLUniformLocation
    ratio: WebGLUniformLocation
    from: WebGLUniformLocation
    to: WebGLUniformLocation
  }
  textures: [WebGLTexture, WebGLTexture]
  buffer: WebGLBuffer
}

export function createGLContext(canvas: HTMLCanvasElement): WebGLRenderingContext | null {
  return canvas.getContext('webgl', { premultipliedAlpha: false, preserveDrawingBuffer: true })
}

export function setExtraUniforms(gl: WebGLRenderingContext, program: WebGLProgram, params: Record<string, unknown>) {
  gl.useProgram(program)
  for (const [key, value] of Object.entries(params)) {
    const loc = gl.getUniformLocation(program, key)
    if (!loc) continue
    if (typeof value === 'number') {
      gl.uniform1f(loc, value)
    } else if (typeof value === 'boolean') {
      gl.uniform1i(loc, value ? 1 : 0)
    } else if (Array.isArray(value)) {
      const arr = value as number[]
      if (arr.length === 2) gl.uniform2fv(loc, arr)
      else if (arr.length === 3) gl.uniform3fv(loc, arr)
      else if (arr.length === 4) gl.uniform4fv(loc, arr)
    }
  }
}

export function compileTransitionProgram(gl: WebGLRenderingContext, glsl: string, defaultParams?: Record<string, unknown>): GLProgram {
  const vs = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER)
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, buildFragmentShader(glsl))

  const program = gl.createProgram()!
  gl.attachShader(program, vs)
  gl.attachShader(program, fs)
  gl.linkProgram(program)
  gl.deleteShader(vs)
  gl.deleteShader(fs)

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const info = gl.getProgramInfoLog(program)
    gl.deleteProgram(program)
    throw new Error(`Program link error: ${info}`)
  }

  gl.useProgram(program)

  const buffer = gl.createBuffer()!
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW)

  const textures: [WebGLTexture, WebGLTexture] = [gl.createTexture()!, gl.createTexture()!]
  for (let i = 0; i < 2; i++) {
    gl.activeTexture(gl.TEXTURE0 + i)
    gl.bindTexture(gl.TEXTURE_2D, textures[i])
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
  }

  if (defaultParams) {
    setExtraUniforms(gl, program, defaultParams)
  }

  return {
    program,
    locations: {
      position: gl.getAttribLocation(program, 'a_position'),
      progress: gl.getUniformLocation(program, 'progress')!,
      ratio: gl.getUniformLocation(program, 'ratio')!,
      from: gl.getUniformLocation(program, 'from')!,
      to: gl.getUniformLocation(program, 'to')!,
    },
    textures,
    buffer,
  }
}

export function drawFrame(
  gl: WebGLRenderingContext,
  prog: GLProgram,
  fromSource: TexImageSource,
  toSource: TexImageSource,
  progress: number,
) {
  gl.viewport(0, 0, gl.canvas.width, gl.canvas.height)
  gl.useProgram(prog.program)
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1)

  gl.activeTexture(gl.TEXTURE0)
  gl.bindTexture(gl.TEXTURE_2D, prog.textures[0])
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, fromSource)
  gl.uniform1i(prog.locations.from, 0)

  gl.activeTexture(gl.TEXTURE1)
  gl.bindTexture(gl.TEXTURE_2D, prog.textures[1])
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, toSource)
  gl.uniform1i(prog.locations.to, 1)

  gl.uniform1f(prog.locations.progress, progress)
  gl.uniform1f(prog.locations.ratio, gl.canvas.width / gl.canvas.height)

  gl.bindBuffer(gl.ARRAY_BUFFER, prog.buffer)
  gl.enableVertexAttribArray(prog.locations.position)
  gl.vertexAttribPointer(prog.locations.position, 2, gl.FLOAT, false, 0, 0)
  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
}

export function dispose(gl: WebGLRenderingContext, prog: GLProgram) {
  gl.deleteTexture(prog.textures[0])
  gl.deleteTexture(prog.textures[1])
  gl.deleteBuffer(prog.buffer)
  gl.deleteProgram(prog.program)
}
